import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader } from "./shared";
import { LifecycleTabs } from "@/components/lifecycle/LifecycleTabs";
import { ArchivedPanel } from "@/components/lifecycle/ArchivedPanel";
import { Send, Archive } from "lucide-react";

export function AdminMessages({ onRead }) {
  const [projects, setProjects] = useState(null);
  const [unread, setUnread] = useState({ by_project: {} });
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const [lifeTab, setLifeTab] = useState("active");
  const endRef = useRef(null);
  const loadUnread = () => api.get("/messages/unread").then((r) => setUnread(r.data)).catch(() => {});
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([])); loadUnread(); }, []);
  useEffect(() => { const t = setInterval(loadUnread, 30000); return () => clearInterval(t); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openThread = async (p) => {
    setActive(p);
    try { const { data } = await api.get(`/projects/${p.id}/messages`); setMessages(data); } catch { setMessages([]); }
    loadUnread(); onRead?.();
  };
  const send = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    try { const { data } = await api.post(`/projects/${active.id}/messages`, { text: msg }); setMessages((m) => [...m, data]); setMsg(""); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (!projects) return <Loader />;
  const visibleProjects = projects.filter((p) => p.thread_lifecycle_status !== "archived");
  const sorted = [...visibleProjects].sort((a, b) => (unread.by_project?.[b.id] || 0) - (unread.by_project?.[a.id] || 0));

  const archiveThread = async () => {
    if (!active) return;
    if (!window.confirm(`Archive the conversation with ${active.org_name}? The full history is preserved and can be restored from the Records Center.`)) return;
    try {
      await api.post(`/lifecycle/message_threads/${active.id}/archive`, { reason: "" });
      toast.success("Conversation archived.");
      setActive(null); setMessages([]);
      const r = await api.get("/projects"); setProjects(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <Head title="Messages" desc="Chat with clients across all their projects." />
      <div className="mb-5"><LifecycleTabs module="message_threads" value={lifeTab} onChange={setLifeTab} refreshKey={projects?.length} /></div>
      {lifeTab !== "active" ? <ArchivedPanel module="message_threads" status={lifeTab} singular="Conversation" allowDelete={false} onChanged={() => api.get("/projects").then((r) => setProjects(r.data))} /> : (
      <div className="grid lg:grid-cols-[300px_1fr] gap-4 h-[calc(100vh-16rem)] min-h-[480px]">
        <div className="bg-navy-800 border border-white/10 overflow-y-auto" data-testid="admin-message-list">
          {sorted.length === 0 ? <div className="p-6 text-gray-500 text-sm">No client projects yet.</div> :
            sorted.map((p) => {
              const u = unread.by_project?.[p.id] || 0;
              return (
                <button key={p.id} onClick={() => openThread(p)} data-testid={`admin-thread-${p.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 flex items-center gap-3 transition-colors ${active?.id === p.id ? "bg-gold/10" : "hover:bg-white/5"}`}>
                  <div className="w-9 h-9 shrink-0 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center text-gold text-sm font-semibold">{(p.org_name || "?")[0]?.toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-sm truncate">{p.org_name}</div>
                    <div className="text-gray-500 text-xs truncate">{p.stage}</div>
                  </div>
                  {u > 0 && <span className="text-[10px] bg-gold text-navy px-2 py-0.5 font-bold" data-testid={`admin-thread-unread-${p.id}`}>{u}</span>}
                </button>
              );
            })}
        </div>
        <div className="bg-navy-800 border border-white/10 flex flex-col min-h-0" data-testid="admin-message-panel">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">Select a conversation to start messaging.</div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
                <span className="text-white font-medium">{active.org_name}</span>
                <button onClick={archiveThread} data-testid="admin-archive-thread" title="Archive conversation" className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-300 border border-white/10 hover:border-red-500/30 px-2.5 py-1.5 transition-colors"><Archive size={13} /> Archive</button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="admin-message-thread">
                {messages.length === 0 ? <p className="text-gray-500 text-center py-8">No messages yet. Say hello!</p> :
                  messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender_role === "admin" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] p-3.5 ${m.sender_role === "admin" ? "bg-gold text-navy" : "bg-navy border border-white/10 text-gray-200"}`}>
                        <div className="text-xs opacity-70 mb-1">{m.sender_name}</div>
                        <div className="text-sm">{m.text}</div>
                      </div>
                    </div>
                  ))}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="flex gap-3 p-3 border-t border-white/10">
                <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type a message…" data-testid="admin-message-input" className="flex-1 bg-navy border border-white/20 text-white p-3 focus:border-gold focus:outline-none" />
                <button type="submit" data-testid="admin-message-send" className="bg-gold text-navy px-5 hover:bg-gold-light transition-colors"><Send size={18} /></button>
              </form>
            </>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
